import React, { useState, useRef, useEffect } from 'react';
import axios from '../api/axios';
import { BACKEND_URL } from '../config';
import './Nexus.css';

function Nexus() {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: '👋 Hallo! Ich bin Nexus, dein KI-Assistent für das Stream Control Center. Wie kann ich dir helfen?'
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);

  const quickHelp = [
    { label: '📝 Command erstellen', topic: 'commands' },
    { label: '💙 Discord Setup', topic: 'discord' },
    { label: '💜 Twitch Setup', topic: 'twitch' },
    { label: '▶️ YouTube Setup', topic: 'youtube' },
    { label: '🎥 OBS verbinden', topic: 'obs' },
    { label: '🎲 Spiele aktivieren', topic: 'games' },
    { label: '⭐ Punkte-System', topic: 'points' }
  ];

  // Scroll to bottom whenever messages change
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const sendMessage = async (question = null) => {
    const messageToSend = question || input;
    if (!messageToSend || !messageToSend.trim()) return;

    const userMessage = messageToSend;
    const newMessages = [...messages, { role: 'user', content: userMessage }];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const history = newMessages.slice(-10).slice(0, -1).map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const response = await axios.post(`${BACKEND_URL}/api/nexus/ask`, {
        question: userMessage,
        history: history
      });

      if (response.data.success) {
        setMessages([...newMessages, {
          role: 'assistant',
          content: response.data.answer
        }]);
      } else {
        setMessages([...newMessages, {
          role: 'assistant',
          content: `❌ Fehler: ${response.data.error}`
        }]);
      }
    } catch (error) {
      setMessages([...newMessages, {
        role: 'assistant',
        content: `❌ Fehler beim Verbinden mit Nexus: ${error.response?.data?.error || error.message}`
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickHelp = async (topic) => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/nexus/help/${topic}`);
      if (response.data.success) {
        setMessages(prev => [...prev,
          { role: 'user', content: `Hilfe zu: ${topic}` },
          { role: 'assistant', content: response.data.answer }
        ]);
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `❌ Fehler: ${response.data.error}`
        }]);
      }
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `❌ Fehler: ${error.response?.data?.error || error.message}`
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage();
  };

  const clearChat = () => {
    setMessages([{
      role: 'assistant',
      content: '👋 Hallo! Ich bin Nexus, dein KI-Assistent für das Stream Control Center. Wie kann ich dir helfen?'
    }]);
  };

  return (
    <div className="nexus-page">
      <div className="page-header">
        <div>
          <h2>🤖 Nexus AI Assistant</h2>
          <p>Dein intelligenter Helfer für das Stream Control Center</p>
        </div>
        <button className="btn-secondary" onClick={clearChat}>
          🗑️ Chat leeren
        </button>
      </div>

      <div className="nexus-container">
        {/* Schnellhilfe Sidebar */}
        <div className="quick-help-section">
          <h3>⚡ Schnellhilfe</h3>
          <div className="quick-help-buttons">
            {quickHelp.map((item, index) => (
              <button
                key={index}
                className="quick-help-btn"
                onClick={() => handleQuickHelp(item.topic)}
                disabled={loading}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {/* Chat */}
        <div className="chat-container">
          {/* Scrollbarer Nachrichten-Bereich */}
          <div
            ref={containerRef}
            className="messages-container"
          >
            {messages.map((message, index) => (
              <div key={index} className={`message ${message.role}`}>
                <div className="message-avatar">
                  {message.role === 'assistant' ? '🤖' : '👤'}
                </div>
                <div className="message-content">
                  <div className="message-text">{message.content}</div>
                </div>
              </div>
            ))}
            {loading && (
              <div className="message assistant">
                <div className="message-avatar">🤖</div>
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form className="input-container" onSubmit={handleSubmit}>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Stelle Nexus eine Frage..."
              disabled={loading}
            />
            <button type="submit" className="btn-primary" disabled={loading || !input.trim()}>
              {loading ? '⏳' : '📤'} Senden
            </button>
          </form>
        </div>
      </div>

      <div className="nexus-footer">
        <p>Powered by Nexus AI</p>
      </div>
    </div>
  );
}

export default Nexus;
